/** Instanced presentation for the dawn herd; every decision stays in DawnHerd. */
import * as THREE from 'three';
import { getAsset } from '@/models/registry';
import { DAWN_HERD_RACE_COUNT, DAWN_HERD_SIZE, HERD_SPACING, type DawnHerdSnapshot } from '@/story/act3/dawnHerd';
import { mergeBySurface } from '@/world/instancing';
import { dawnHerdLanePointAt, RACE_GATE_AT } from '@/world/loadingYard';
const GONE = 0.0001;

export class DawnHerdView {
  readonly group = new THREE.Group();
  readonly #meshes: THREE.InstancedMesh[] = [];
  readonly #matrix = new THREE.Matrix4();
  readonly #position = new THREE.Vector3();
  readonly #quaternion = new THREE.Quaternion();
  readonly #euler = new THREE.Euler();
  readonly #scale = new THREE.Vector3(1, 1, 1);

  constructor() {
    const asset = getAsset('finisher-herd');
    if (!asset) throw new Error('dawn herd: finisher-herd asset is not registered');
    this.group.name = 'yard-dawn-herd';
    for (const batch of mergeBySurface(asset.create())) {
      const mesh = new THREE.InstancedMesh(batch.geometry, batch.material, DAWN_HERD_SIZE);
      mesh.name = `dawn-herd-${batch.name}`;
      mesh.castShadow = batch.castShadow;
      mesh.receiveShadow = batch.receiveShadow;
      // Every instance is rewritten as the queue advances. A construction-time
      // sphere would still surround the race and cull the stream at the gate.
      mesh.frustumCulled = false;
      this.#meshes.push(mesh);
      this.group.add(mesh);
    }
    // A room can render once before its first fixed step. Seed the same queue
    // shape as a construction snapshot so that frame has race bodies, not a
    // flash of forty identity matrices at the world origin.
    this.update(0, { members: Array.from({ length: DAWN_HERD_SIZE }, (_, index) => ({ index, state: index < DAWN_HERD_RACE_COUNT ? 'race' : 'shed', distance: -0.6 - index * HERD_SPACING })), headDistance: null, throughs: 0, atVehicleGate: false, drawn: false });
  }

  update(_frameDt: number, snapshot: DawnHerdSnapshot): void {
    for (const member of snapshot.members) this.#place(member.index, member.state, member.distance);
    for (const mesh of this.#meshes) mesh.instanceMatrix.needsUpdate = true;
  }
  /**
   * Place the first `poses.length` members at authored yard positions and hide
   * the rest — for a beat that stages the herd rather than queueing it (beat
   * 13's reversal and pack). `clock` drives the walking bob of a member marked
   * `moving`, so a stopped animal stands still.
   */
  updatePoses(poses: readonly { readonly x: number; readonly z: number; readonly heading: number; readonly moving: boolean }[], clock: number): void {
    for (let index = 0; index < DAWN_HERD_SIZE; index += 1) {
      const pose = poses[index];
      if (pose === undefined) this.#hide();
      else this.#pose(index, pose.x, pose.z, pose.heading, pose.moving ? clock * 1.6 : null);
      this.#matrix.compose(this.#position, this.#quaternion, this.#scale);
      for (const mesh of this.#meshes) mesh.setMatrixAt(index, this.#matrix);
    }
    for (const mesh of this.#meshes) mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    for (const mesh of this.#meshes) { mesh.geometry.dispose(); mesh.dispose(); }
    this.#meshes.length = 0;
    this.group.clear();
    this.group.removeFromParent();
  }

  #place(index: number, state: DawnHerdSnapshot['members'][number]['state'], distance: number): void {
    if (state === 'shed') this.#hide();
    else {
      const at = state === 'race' ? { x: RACE_GATE_AT.x, z: RACE_GATE_AT.z + distance, heading: 0 } : dawnHerdLanePointAt(distance);
      const lateral = state === 'out' ? ((index * 17) % 5 - 2) * 0.06 : 0;
      // Distance, rather than frame time, makes a stopped race pig still. The
      // same authored stride reads on the queue advancing into Holly's throat.
      this.#pose(index, at.x + Math.cos(at.heading) * lateral, at.z - Math.sin(at.heading) * lateral, at.heading, distance);
    }
    this.#matrix.compose(this.#position, this.#quaternion, this.#scale);
    for (const mesh of this.#meshes) mesh.setMatrixAt(index, this.#matrix);
  }

  #hide(): void {
    this.#position.set(0, -20, 0); this.#quaternion.identity(); this.#scale.setScalar(GONE);
  }

  /** `gait` is the stride's phase in metres (or any monotonic clock); null stands the animal still. */
  #pose(index: number, x: number, z: number, heading: number, gait: number | null): void {
    const swing = gait === null ? 0 : Math.sin(gait * Math.PI * 2.2 + index);
    this.#position.set(x, Math.abs(swing) * 0.02, z);
    this.#euler.set(0, heading, swing * 0.03, 'YXZ');
    this.#quaternion.setFromEuler(this.#euler); this.#scale.set(1, 1, 1);
  }
}
